import { Box, FormHelperText, Typography } from "@mui/material";
import { ReactNode } from "react";

import {
  settingsControlFontSize,
  settingsOverviewFormControlMaxWidthPx,
  settingsOverviewHelperFontSize,
  settingsOverviewLabelColumnPx,
  settingsOverviewLabelControlColumnGap,
  settingsOverviewRowGridTemplateColumns
} from "~/features/settings/settings.styles";

type Props = {
  id?: string;
  labelId?: string;
  label: ReactNode;
  helperText?: string;
  /** When true, helper text uses error styling (e.g. validation message in the helper slot). */
  helperTextError?: boolean;
  /**
   * Reserve vertical space for this many lines of helper text so swapping
   * helper ↔ error copy does not shift the page.
   */
  helperTextMinLines?: number;
  /** When true, helper text stays on one line (e.g. short copy beside vertical stacks). */
  helperTextNoWrap?: boolean;
  children: ReactNode;
};

export function OverviewFieldRow({
  id,
  labelId,
  label,
  helperText,
  helperTextError = false,
  helperTextMinLines,
  helperTextNoWrap,
  children
}: Props) {
  const showHelper = helperText != null && helperText.length > 0;

  if (!showHelper) {
    return (
      <Box
        id={id}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: { xs: "wrap", sm: "nowrap" },
          columnGap: settingsOverviewLabelControlColumnGap,
          rowGap: 1,
          alignItems: { xs: "stretch", sm: "center" },
          width: "100%",
          minWidth: 0
        }}
      >
        <Typography
          id={labelId}
          component="span"
          variant="body2"
          sx={{
            color: "text.primary",
            fontWeight: 400,
            fontSize: settingsControlFontSize,
            lineHeight: 1.35,
            flexShrink: 0,
            whiteSpace: { sm: "normal" },
            width: { xs: "100%", sm: `${settingsOverviewLabelColumnPx}px` },
            minWidth: 0,
            maxWidth: { sm: `${settingsOverviewLabelColumnPx}px` },
            boxSizing: "border-box"
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            flex: { xs: 1, sm: "1 1 0" },
            minWidth: 0,
            maxWidth: { sm: settingsOverviewFormControlMaxWidthPx },
            width: { xs: "100%", sm: "auto" },
            alignSelf: { xs: "stretch", sm: "center" }
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      id={id}
      sx={{
        display: "grid",
        justifyContent: { xs: "stretch", sm: "start" },
        justifyItems: { xs: "stretch", sm: "start" },
        gridTemplateColumns: settingsOverviewRowGridTemplateColumns,
        columnGap: settingsOverviewLabelControlColumnGap,
        rowGap: 1,
        width: "100%",
        minWidth: 0
      }}
    >
      <Typography
        id={labelId}
        component="span"
        variant="body2"
        sx={{
          color: "text.primary",
          fontWeight: 400,
          fontSize: settingsControlFontSize,
          lineHeight: 1.35,
          gridColumn: { xs: "1", sm: "1" },
          gridRow: { xs: "1", sm: "1" },
          alignSelf: "center",
          justifySelf: { sm: "start" },
          whiteSpace: { sm: "normal" },
          width: { sm: `${settingsOverviewLabelColumnPx}px` },
          minWidth: 0,
          maxWidth: { sm: `${settingsOverviewLabelColumnPx}px` },
          boxSizing: "border-box"
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          gridColumn: { xs: "1", sm: "2" },
          gridRow: { xs: "2", sm: "1" },
          minWidth: 0,
          width: {
            xs: "100%",
            sm: `min(100%, ${settingsOverviewFormControlMaxWidthPx}px)`
          },
          alignSelf: "center",
          justifySelf: { sm: "start" }
        }}
      >
        {children}
      </Box>
      <FormHelperText
        component="div"
        error={helperTextError}
        sx={{
          gridColumn: { xs: "1", sm: "2" },
          gridRow: { xs: "3", sm: "2" },
          m: 0,
          width: {
            xs: "100%",
            sm: `min(100%, ${settingsOverviewFormControlMaxWidthPx}px)`
          },
          minWidth: 0,
          ...(helperTextMinLines != null
            ? {
                minHeight: `calc(${settingsOverviewHelperFontSize} * 1.4 * ${helperTextMinLines})`
              }
            : {}),
          justifySelf: { sm: "start" },
          display: "block",
          fontSize: settingsOverviewHelperFontSize,
          lineHeight: 1.4,
          color: helperTextError ? "error.main" : "text.secondary",
          ...(helperTextNoWrap
            ? { whiteSpace: "nowrap", overflowX: "auto" }
            : {})
        }}
      >
        {helperText}
      </FormHelperText>
    </Box>
  );
}
